import axios from 'axios';
import fingerprintjs from '@fingerprintjs/fingerprintjs';
import platform from 'platform';
import config from '../config';
import { getUser } from '../helpers/storage';

const { apiHost } = config;

export function getUserToken() {
  return getUser()?.token;
}

function hashString(str: string) {
  let hash = 0;
  if (str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash;
}

async function getFingerprint(email: string) {
  const value = await fingerprintjs.load();
  const result = await value.get();

  // added email to hash so that when a user tries to login
  // with a different account in a incognito browser, it doesn't
  // invalidate the one in the normal browser (since visitorId
  // is the same for a browser not regarding incognito/normal mode)
  return `${result.visitorId}${hashString(email)}`;
}

const getHeaderWithAuthentication = () =>
  getUserToken()
    ? {
        Authorization: `Bearer ${getUserToken()}`,
      }
    : {};

const getOs = () => {
  return `${platform.os?.family} ${platform.os?.version}`;
};

const getPlatformDescription = () => {
  return platform.description;
};

export function getData() {
  return axios.get(`${apiHost}/session/data`);
}

export function getPlans() {
  return axios.get(`${apiHost}/session/plans`);
}

export function getCountries() {
  return axios.get(`${apiHost}/session/countries`);
}

export function register(attrs: any) {
  return getFingerprint(attrs.email)
    .then((fingerprint) =>
      axios.post(`${apiHost}/session/register`, {
        ...attrs,
        fingerprint,
        os: getOs(),
        platform: getPlatformDescription(),
      })
    )
    .then((response) => response.data.data);
}

export function login(attrs: any) {
  return getFingerprint(attrs.email)
    .then((fingerprint) =>
      axios.post(`${apiHost}/session/login`, {
        ...attrs,
        fingerprint,
        os: getOs(),
        platform: getPlatformDescription(),
      })
    )
    .then((response) => response.data.data);
}

export function logout() {
  return axios.get(`${apiHost}/session/logout`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function me(token?: string) {
  return axios.get(`${apiHost}/session/me`, {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : getHeaderWithAuthentication(),
  });
}

export function meUpdate(data: any) {
  return axios.put(`${apiHost}/session/me`, data, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getUserSessions() {
  return axios.get(`${apiHost}/session/me/sessions`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function forgotPasswordSendEmail(email: string) {
  return axios.post(`${apiHost}/session/forgot`, { email });
}

export function forgotPasswordGetEmail(id: string, code: string) {
  return axios.get(`${apiHost}/session/forgot/${id}/${code}`);
}

export function forgotPasswordUpdatePassword(
  id: string,
  code: string,
  data: { email: string; password: string }
) {
  return axios.post(`${apiHost}/session/forgot/${id}/${code}`, data);
}

export function updatePassword(oldPassword: string, newPassword: string) {
  return axios.post(
    `${apiHost}/session/me/password`,
    {
      old_password: oldPassword,
      new_password: newPassword,
    },
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerGetProfile(profileId: string) {
  return axios.get(`${apiHost}/seller/profile/${profileId}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function createSellerProperty(attrs: object) {
  return axios.post(
    `${apiHost}/seller/property`,
    { ...attrs },
    { headers: getHeaderWithAuthentication() }
  );
}

export function editSellerProperty(id: string, attrs: object) {
  return axios.put(
    `${apiHost}/seller/property/${id}`,
    { ...attrs },
    { headers: getHeaderWithAuthentication() }
  );
}

export function getSellerProperty(id: string) {
  return axios.get(`${apiHost}/seller/property/${id}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function deleteSellerProperty(id: string, params: any) {
  return axios.delete(`${apiHost}/seller/property/${id}`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function getSellerProperties(params = {}) {
  return axios.get(`${apiHost}/seller/property`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function getTypeProperties() {
  return axios.get(`${apiHost}/property/typeProperty`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getProperties(params = {}) {
  return axios.get(`${apiHost}/property`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function getProperty(id: string) {
  return axios.get(`${apiHost}/property/${id}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getPropertyNoBimmo(id: string) {
  return axios.get(`${apiHost}/propertyNoBimmo/${id}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function addOneSeenNoBimmo(id: string) {
  return axios.post(`${apiHost}/propertyNoBimmo/${id}/seen`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function addOneRedirectNoBimmo(id: string) {
  return axios.post(`${apiHost}/propertyNoBimmo/${id}/redirect`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getFavProperties(params = {}) {
  return axios.get(`${apiHost}/property/favorite`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function publishProperty(id: string) {
  return axios.get(`${apiHost}/seller/property/${id}/publish`, {
    headers: getHeaderWithAuthentication(),
  });
}

// Chat endpoints

export function startChat(userId: string, propertyId: string) {
  return axios.post(
    `${apiHost}/chat/start`,
    { user: userId, property: propertyId },
    { headers: getHeaderWithAuthentication() }
  );
}

export function getChatList(params = {}) {
  return axios.get(`${apiHost}/chat`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function sendChatMessage(chatId: string, message: string) {
  return axios.post(
    `${apiHost}/chat/${chatId}`,
    { message, type: 'user' },
    { headers: getHeaderWithAuthentication() }
  );
}

export function sendChatAdminMessage(chatId: string, message: string) {
  return axios.post(
    `${apiHost}/chat/${chatId}`,
    { message, type: 'admin' },
    { headers: getHeaderWithAuthentication() }
  );
}

export function getNotificationFromApi() {
  return axios.get(`${apiHost}/chat/message/user/sent`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getChat(chatId: string) {
  return axios.get(`${apiHost}/chat/${chatId}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getChatMessages(chatId: string, params = {}) {
  return axios.get(`${apiHost}/chat/${chatId}/message`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function favorite(propertyId: string) {
  return axios.get(`${apiHost}/property/${propertyId}/favorite`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function uploadFile(data: FormData) {
  return axios.post(`${apiHost}/upload/image`, data, {
    headers: {
      ...getHeaderWithAuthentication(),
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function uploadGeneral(data: FormData) {
  return axios.post(`${apiHost}/upload/general`, data, {
    headers: {
      ...getHeaderWithAuthentication(),
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function uploadBuyerDocuments(
  propertyId: string,
  data: { name: string; mime: string; uri: string; reference: string }
) {
  return axios.post(`${apiHost}/property/${propertyId}/document/deed`, data, {
    headers: getHeaderWithAuthentication(),
  });
}

export function uploadSellerDocuments(
  propertyId: string,
  data: { name: string; mime: string; uri: string; reference: string }
) {
  return axios.post(
    `${apiHost}/seller/property/${propertyId}/document/deed`,
    data,
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerDeleteDocument(propertyId: string, documentId: string) {
  return axios.delete(
    `${apiHost}/seller/property/${propertyId}/document/deed/${documentId}`,
    {
      headers: getHeaderWithAuthentication(),
    }
  );
}

export function buyerDeleteDocument(propertyId: string, documentId: string) {
  return axios.delete(
    `${apiHost}/property/${propertyId}/document/deed/${documentId}`,
    {
      headers: getHeaderWithAuthentication(),
    }
  );
}

export function validateEmail(id: string, code: string) {
  return axios.get(`${apiHost}/validation/email/${id}/${code}`);
}

export function validatePhone(code: string) {
  return axios.get(`${apiHost}/validation/phone/${code}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function resendValidation(type: 'phone' | 'email') {
  return axios.get(`${apiHost}/validation/resend/${type}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getAppointmentDisponibility(propertyId: string, date: string) {
  return axios.get(
    `${apiHost}/meeting/property/${propertyId}/availability/${date}`,
    { headers: getHeaderWithAuthentication() }
  );
}

export function reserveAppointment(
  propertyId: string,
  date: string,
  data: {
    meeting: number;
    body: string;
    type: 'virtual' | 'presencial';
    information: { phone?: string; email: string };
    chatId: string;
  }
) {
  return axios.post(
    `${apiHost}/meeting/property/${propertyId}/reserve/${date}`,
    data,
    { headers: getHeaderWithAuthentication() }
  );
}

export function getUserAppointments(params = {}) {
  return axios.get(`${apiHost}/meeting`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function deleteAppointment(appointmentId: string) {
  return axios.delete(`${apiHost}/meeting/cancel/${appointmentId}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getSellerAppointments(params = {}) {
  return axios.get(`${apiHost}/seller/property/meeting`, {
    headers: getHeaderWithAuthentication(),
    params,
  });
}

export function getPlacesAutocomplete(searchStr: string) {
  return axios.get(`${apiHost}/property/placesearch`, {
    params: { search: searchStr },
    headers: getHeaderWithAuthentication(),
  });
}

export function getDirectionsAutocomplete(searchStr: string) {
  return axios.post(
    `${apiHost}/maps/place/autocomplete`,
    { input: searchStr },
    { headers: getHeaderWithAuthentication() }
  );
}

export function getGeoPositionByPlaceId(placeId: string) {
  return axios.post(
    `${apiHost}/maps/place/${placeId}`,
    {},
    { headers: getHeaderWithAuthentication() }
  );
}

export function getOffers() {
  return axios.get(`${apiHost}/property/offer`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getOfferByProperty(propertyId: string) {
  // return Promise.resolve({ data: { data: {} } });
  // TODO:
  // return Promise.resolve({data: { data: { id: 2423423, offer: 15000000, status: 'pendiente' } },});
  return axios.get(`${apiHost}/property/${propertyId}/offer`, {
    headers: getHeaderWithAuthentication(),
  });
}

//Get separation by property
export function getSeparationByProperty(propertyId: string) {
  // return Promise.resolve({ data: { data: {} } });
  // TODO:
  // return Promise.resolve({data: { data: { id: 2423423, offer: 15000000, status: 'pendiente' } },});
  return axios.get(`${apiHost}/separation/${propertyId}/`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function acceptRejectOffer(
  propertyId: string,
  status: 'aceptada' | 'rechazada'
) {
  return axios.post(
    `${apiHost}/property/${propertyId}/offer`,
    { status },
    { headers: getHeaderWithAuthentication() }
  );
}

export function purchaseProperty(propertyId: string) {
  // TODO: {"success":true,"status":200,"duration":26,"data":{"property":"5fb5af9faff6f1423a9514dc","user":"600856ab732c3d38f892fbb9","offer":20000000,"status":"aceptada","_id":"6008628fb6f2c8393b0b47b5","updatedAt":"2021-01-20T17:04:15.730Z","createdAt":"2021-01-20T17:04:15.730Z","id":"6008628fb6f2c8393b0b47b5"}}
  return axios.post(
    `${apiHost}/property/${propertyId}/purchase`,
    {},
    { headers: getHeaderWithAuthentication() }
  );
}

export function getPropertyDraftPromesaCompraVenta(propertyId: string) {
  return axios.get(`${apiHost}/property/${propertyId}/promise`, {
    headers: getHeaderWithAuthentication(),
    responseType: 'blob',
  });
}

export function sellerMakeOfferToUser(
  userId: string,
  propertyId: string,
  attrs: { price: number }
) {
  return axios.post(
    `${apiHost}/seller/property/${propertyId}/offer`,
    { offer: attrs.price, user: userId },
    { headers: getHeaderWithAuthentication() }
  );
}

export function cancelSeparationToUser(
  separationId: string,
  propertyId: string
) {
  return axios.put(
    `${apiHost}/separation/cancel/${separationId}&${propertyId}`,
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerEditOffer(
  propertyId: string,
  offerId: string,
  attrs: { price: number }
) {
  return axios.post(
    `${apiHost}/seller/property/${propertyId}/offer/${offerId}`,
    { offer: attrs.price },
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerGetOffers() {
  return axios.get(`${apiHost}/seller/property/offer`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function sellerGetPropertyOffers(propertyId: string) {
  return axios.get(`${apiHost}/seller/property/${propertyId}/offer`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function sellerGetOfferByUser(propertyId: string, userId: string) {
  return axios.get(
    `${apiHost}/seller/property/${propertyId}/offer/user/${userId}`,
    {
      headers: getHeaderWithAuthentication(),
    }
  );
}

export function sellerApproveSeparationPayment(propertyId: string) {
  return axios.get(
    `${apiHost}/seller/property/${propertyId}/separation/payment/approved`,
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerCancelPurchaseProcess(propertyId: string) {
  return axios.get(`${apiHost}/property/${propertyId}/cancel`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function sellerConfirmDocuments(propertyId: string) {
  return axios.get(
    `${apiHost}/seller/property/${propertyId}/document/confirm`,
    {
      headers: getHeaderWithAuthentication(),
    }
  );
}

export function confirmDocuments(propertyId: string) {
  return axios.get(`${apiHost}/property/${propertyId}/document/confirm`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function confirmNotaryPayment(propertyId: string, reference: string) {
  return axios.post(
    `${apiHost}/property/${propertyId}/notary/confirm_payment`,
    { reference },
    { headers: getHeaderWithAuthentication() }
  );
}

export function sellerConfirmNotaryPayment(
  propertyId: string,
  reference: string
) {
  return axios.post(
    `${apiHost}/seller/property/${propertyId}/notary/confirm_payment`,
    { reference },
    { headers: getHeaderWithAuthentication() }
  );
}

export function confirmLastPayment(propertyId: string) {
  return axios.get(`${apiHost}/property/${propertyId}/verify_last_payment`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function sellerConfirmLastPayment(propertyId: string) {
  return axios.get(
    `${apiHost}/seller/property/${propertyId}/confirm_last_payment`,
    {
      headers: getHeaderWithAuthentication(),
    }
  );
}

export function chatAttach(
  chatId: string,
  data: {
    user: string;
    uri: string;
    reference: string;
    type: string;
    mime: string;
    name: string;
  }
) {
  return axios.post(`${apiHost}/chat/${chatId}/attach`, data, {
    headers: getHeaderWithAuthentication(),
  });
}

export function deleteChatAttachment(chatId: string, attachId: string) {
  return axios.delete(`${apiHost}/chat/${chatId}/attach/${attachId}`, {
    headers: getHeaderWithAuthentication(),
  });
}

export function getAddressMap(address: string, key: string) {
  return axios.get(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${key}`
  );
}

// TODO: cancelar proceso de venta
// /v1/property/:id/cancel

// TODO: revisar mensajes del comprador al iniciar chat
